import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Progress, Text } from '@chakra-ui/react';

interface TimerBarProps {
  deadline: number; // epoch ms
  seconds: number; // total seconds
  color?: string; // css color or chakra token for the fill
  height?: string | number;
  showLabel?: boolean;
  labelPrefix?: string; // e.g., "Catégorie" or "Question"
}

// Lightweight, smooth countdown bar driven by a server-provided deadline.
const TimerBar: React.FC<TimerBarProps> = ({
  deadline,
  seconds,
  color = 'var(--bleu-electrique)',
  height = '10px',
  showLabel = true,
  labelPrefix,
}) => {
  const totalMs = Math.max(1, seconds * 1000);
  const [now, setNow] = useState<number>(() => Date.now());
  const rafRef = useRef<number | null>(null);

  const remainingMs = Math.max(0, deadline - now);
  const percent = useMemo(() => Math.max(0, Math.min(100, (remainingMs / totalMs) * 100)), [remainingMs, totalMs]);
  const secondsLeft = Math.ceil(remainingMs / 1000);

  useEffect(() => {
    const tick = () => {
      setNow(Date.now());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [deadline, seconds]);

  // Color shift when low time
  const barColor = remainingMs <= 4000 ? '#E53E3E' : color; // red when < 4s

  return (
    <Box w="full">
      {showLabel && (
        <Flex justify="space-between" align="center" mb={1} px={1}>
          <Text fontSize="sm" opacity={0.85}>
            {labelPrefix || 'Timer'}
          </Text>
          <Text fontSize="sm" fontWeight="bold">{secondsLeft}s</Text>
        </Flex>
      )}
      <Progress
        value={percent}
        size="sm"
        height={height}
        borderRadius="full"
        bg="rgba(255,255,255,0.1)"
        hasStripe={false}
        isAnimated={false}
        sx={{
          '& > div': {
            backgroundColor: barColor,
          },
        }}
      />
    </Box>
  );
};

export default TimerBar;
